import React, { useState, useEffect, useMemo } from "react";
import { Grid, Typography, Dialog, IconButton } from "@mui/material";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  getClientAndCoClient,
  saveClient,
  saveCoClient,
  saveFamilyMember,
  saveOtherMember,
} from "../../api/advisor/client";
import SelectParticipantStep from "./SelectParticipantStep";
import AddNewClientForm from "@instream-solutions/instream-design-system/lib/Components/AddNewClientForm/AddNewClientForm";
import * as actionTypes from "../../redux/constants";
import { getAdvisorDefaultPlanSettings } from "../../api/advisor/defaultPlanSettings";
import AddFamilyMemberForm from "@instream-solutions/instream-design-system/lib/Components/AddFamilyMemberForm/AddFamilyMemberForm";
import AddOtherMemberForm from "@instream-solutions/instream-design-system/lib/Components/AddOtherMemberForm/AddOtherMemberForm";
import Loader from "../Utility/Loader/Loader";
import ClientWizardComplete from "./ClientWizardComplete";
import { useParams, withRouter } from "react-router-dom";
import "./AddNewClientWizard.scss";
import { Close as CloseIcon } from "@mui/icons-material";
import { LeavePageModal } from "../Modals/ConfirmModal/ConfirmModal";
import { translateRelationshipType } from "../../common/shared";

const STEP_ADD_CLIENT = 0;
const STEP_SELECT_PEOPLE = 1;
const STEP_ADD_CO_CLIENT = 2;
const STEP_ADD_FAMILY = 3;
const STEP_ADD_OTHER = 4;
const STEP_FINAL = 5;

const AddNewClientWizard = (props) => {
  const [state, setState] = useState({
    isLoading: true,
    currentStep: STEP_ADD_CLIENT,
    addCoClient: false,
    addFamilyMember: false,
    addOther: false,
    hasCoClient: false,
    defaultSettings: {},
    coClient: null,
  });

  const [showLeavePageModal, updateShowLeavePageModal] = useState(false);

  const { t } = useTranslation();

  const currentUser = props.currentUser.toJS();
  const currentClient = props.currentClient.toJS();

  useEffect(() => {
    getAdvisorDefaultPlanSettings(currentUser.advisor.advisorId).then(
      (response) => {
        if (!response) {
          props.closeAddNewClientWizard();
          return;
        }
        setState({
          ...state,
          isLoading: false,
          defaultSettings: response.defaultSettings,
        });
      }
    );
  }, []);

  const saveClientAndGoToWizard = (clientData) => {
    saveClient(currentUser.advisor.advisorId, clientData).then((client) => {
      client.relationshipType = {
        id: "CLIENT",
        name: t("ClientDetails.Profile.Family.Relationship.client"),
      };
      props.updateClient(client);
      props.removeCoClient();
      props.removeAllFamilyMember();
      next();
    });
  };

  const saveCoClientAndGoToNextStep = (coClientData) => {
    saveCoClient(currentUser.advisor.advisorId, currentClient.client.id, {
      ...coClientData,
      coClientId: null,
    }).then((coClient) => {
      setState({
        ...state,
        coClient: coClient,
      });
      next();
    });
  };

  const forward_state_transition = [
    () => {
      return STEP_SELECT_PEOPLE;
    },
    () => {
      if (state.addCoClient && !state.hasCoClient) return STEP_ADD_CO_CLIENT;
      if (state.addFamilyMember) return STEP_ADD_FAMILY;
      if (state.addOther) return STEP_ADD_OTHER;
      reloadFamilyList();
      return STEP_FINAL;
    },
    () => {
      if (state.addFamilyMember) return STEP_ADD_FAMILY;
      if (state.addOther) return STEP_ADD_OTHER;
      reloadFamilyList();
      return STEP_FINAL;
    },
    () => {
      if (state.addOther) return STEP_ADD_OTHER;
      reloadFamilyList();
      return STEP_FINAL;
    },
    () => {
      reloadFamilyList();
      return STEP_FINAL;
    },
  ];

  const reloadFamilyList = () => {
    getClientAndCoClient(
      currentUser.advisor.advisorId,
      currentClient.client.id
    ).then((response) => {
      response.client.relationshipType = translateRelationshipType(
        response.client.relationshipType,
        t
      );
      props.updateClient(response.client);
      response.coClient.relationshipType = translateRelationshipType(
        response.coClient.relationshipType,
        t
      );
      props.updateCoClient(response.coClient);
      response.family.map((member) => {
        member.relationshipType = translateRelationshipType(
          member.relationshipType,
          t
        );
        return member;
      });
      props.updateFamilyMember(response.family);
    });
  };

  const gotoClientList = () => {
    setTimeout(() => {
      let { history } = props;
      history.push({
        pathname: `/advisor/clients`,
      });
    }, 50);
  };

  const next = () => {
    const nextStep = forward_state_transition[state.currentStep]();
    setState({
      ...state,
      hasCoClient: state.hasCoClient
        ? true
        : state.currentStep === STEP_ADD_CO_CLIENT,
      currentStep: nextStep,
    });
  };

  const cancel = () => {
    props.closeAddNewClientWizard();
    props.triggerClientListRefresh();
    gotoClientList();
  };

  const resetToAddParticipants = () => {
    setState({
      ...state,
      currentStep: STEP_SELECT_PEOPLE,
      addFamilyMember: false,
      addOther: false,
    });
  };

  const completeWizard = () => {
    props.closeAddNewClientWizard();
    props.openPlanCreationModal();
    setTimeout(() => {
      let { history } = props;
      history.push({
        pathname: `/advisor/clients/details/${currentClient.client.id}/profile/network/family`,
      });
    }, 50);
  };

  const wizardStepContainerClass = `client-wizard-step-container step-${state.currentStep}`;

  console.log("CurrentClient: ", props.currentClient.toJS());

  return (
    <Dialog
      id="add-new-client-modal"
      className={"instream-modal-dialog"}
      maxWidth="lg"
      fullWidth
      open={true}
      onClose={(event, reason) => {
        if (reason === "backdropClick") return;
        updateShowLeavePageModal(true);
      }}
    >
      <Grid className={wizardStepContainerClass} container spacing={3}>
        <Grid item xs={12}>
          <IconButton
            color="inherit"
            onClick={() => {
              updateShowLeavePageModal(true);
            }}
            className="close-button"
            size="large"
          >
            <CloseIcon />
          </IconButton>
        </Grid>
        {state.isLoading && (
          <Grid item xs={12}>
            <Loader />
          </Grid>
        )}
        <Grid item xs={12}>
          {!state.isLoading && (
            <React.Fragment>
              {state.currentStep === 0 && (
                <AddNewClientForm
                  client={props.currentClient}
                  coClient={null}
                  save={(client) => {
                    saveClientAndGoToWizard(client);
                  }}
                  saveLabel={"Next"}
                  close={() => {
                    props.closeAddNewClientWizard();
                    gotoClientList();
                  }}
                  defaultRetirementAge={
                    state.defaultSettings.clientKeyDates.retirementAge
                  }
                  defaultLifeExpectancyAge={
                    state.defaultSettings.clientKeyDates.lifeExpectancyAge
                  }
                />
              )}
              {state.currentStep === STEP_SELECT_PEOPLE && (
                <SelectParticipantStep
                  state={state}
                  setState={setState}
                  next={next}
                  cancel={cancel}
                />
              )}
              {state.currentStep === STEP_ADD_CO_CLIENT && (
                <AddNewClientForm
                  client={props.currentClient}
                  coClient={null}
                  isCoClient={true}
                  save={(client) => {
                    saveCoClientAndGoToNextStep(client);
                  }}
                  saveLabel={"Next"}
                  close={() => {
                    next();
                  }}
                  defaultRetirementAge={
                    state.defaultSettings.clientKeyDates.retirementAge
                  }
                  defaultLifeExpectancyAge={
                    state.defaultSettings.clientKeyDates.lifeExpectancyAge
                  }
                  defaults={{
                    addressLine:
                      currentClient.client.addressList[0].addressLine,
                    zipCode: currentClient.client.addressList[0].zipCode,
                    city: currentClient.client.addressList[0].city,
                    state: currentClient.client.addressList[0].state,
                    country: currentClient.client.addressList[0].country,
                  }}
                />
              )}
              {state.currentStep === STEP_ADD_FAMILY && (
                <AddFamilyMemberForm
                  member={null}
                  save={(member) => {
                    saveFamilyMember(
                      currentUser.advisor.advisorId,
                      currentClient.client.id,
                      null,
                      member
                    ).then((familyMember) => {
                      next();
                    });
                  }}
                  saveLabel={"Next"}
                  close={() => {
                    next();
                  }}
                />
              )}
              {state.currentStep === STEP_ADD_OTHER && (
                <AddOtherMemberForm
                  member={null}
                  save={(member) => {
                    saveOtherMember(
                      currentUser.advisor.advisorId,
                      currentClient.client.id,
                      null,
                      member
                    ).then((otherMember) => {
                      next();
                    });
                  }}
                  saveLabel={"Next"}
                  close={() => {
                    next();
                  }}
                />
              )}
              {state.currentStep === STEP_FINAL && (
                <ClientWizardComplete
                  resetToAddParticipants={resetToAddParticipants}
                  completeWizard={completeWizard}
                  close={() => {}}
                />
              )}
            </React.Fragment>
          )}
        </Grid>
      </Grid>
      {showLeavePageModal && (
        <LeavePageModal
          actionOk={() => {
            cancel();
          }}
          actionCancel={() => {
            updateShowLeavePageModal(false);
          }}
        />
      )}
    </Dialog>
  );
};

const mapStateToProps = (state) => {
  return {
    currentUser: state.currentUserReducer,
    currentClient: state.currentClientReducer,
    windowParameters: state.uxStateReducer.get("windowParameters"),
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    updateClient: (client) =>
      dispatch({
        type: actionTypes.SET_CURRENT_CLIENT,
        data: client,
      }),
    updateCoClient: (coClient) =>
      dispatch({
        type: actionTypes.SET_CURRENT_COCLIENT,
        data: coClient,
      }),
    updateFamilyMember: (member) =>
      dispatch({
        type: actionTypes.SET_CURRENT_FAMILY,
        data: member,
      }),
    removeCoClient: () =>
      dispatch({
        type: actionTypes.REMOVE_COCLIENT,
      }),
    removeAllFamilyMember: () =>
      dispatch({
        type: actionTypes.REMOVE_ALL_FAMILY_MEMBER,
      }),
    updateWindowPosition: (e) =>
      dispatch({
        type: actionTypes.UPDATE_WINDOW_POSITION,
        data: e,
      }),
    updateWindowSize: (e) =>
      dispatch({
        type: actionTypes.UPDATE_WINDOW_SIZE,
        data: e,
      }),
    closeAddNewClientWizard: () =>
      dispatch({
        type: actionTypes.SET_CLIENT_WIZARD_VISIBILITY,
        value: false,
      }),
    triggerClientListRefresh: () =>
      dispatch({
        type: actionTypes.TRIGGER_CLIENT_LIST_REFRESH,
      }),
    openPlanCreationModal: () =>
      dispatch({
        type: actionTypes.SHOW_PLAN_CREATION_MODAL,
        value: null,
      }),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(AddNewClientWizard));
